import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Images live at `ideas/{ideaId}/images/{uuid}`, with no user segment in the path.
 *
 * That is the iOS convention and what the deployed Storage rules govern — *not* the
 * `users/{uid}/ideas/{ideaId}/` path the old Expo client wrote to, which production denies.
 * The bucket confirms it: every existing object is under `ideas/`.
 *
 * Because the path carries no owner, the rules establish ownership two ways: `ownsIdea()`
 * checks in Firestore that the caller owns the idea document, and uploads stamp `ownerId` in
 * object metadata so the uploader can still delete their own bytes if the document goes away.
 * Both are reproduced here — omitting the stamp would still upload, but would leave an object
 * only `ownsIdea()` could ever remove.
 */

/** Mirrors `isUnder10MB()` in storage.rules. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Check a file before uploading, so a violation reads as a sentence rather than as an
 * opaque `storage/unauthorized` from the rules.
 *
 * Returns null when the file is fine.
 */
export function rejectReason(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return `${file.name} is not an image.`;
  }
  if (file.size >= MAX_IMAGE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return `${file.name} is ${mb} MB. The limit is 10 MB.`;
  }
  return null;
}

/**
 * The extension iOS would have used, so objects in the bucket stay inspectable by name.
 * Nothing reads it — `contentType` is what decides how the object is served.
 */
function extensionFor(file: File): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop() : null;
  if (fromName && /^[a-z0-9]{1,5}$/i.test(fromName)) return `.${fromName.toLowerCase()}`;
  const fromType = file.type.split('/')[1];
  return fromType ? `.${fromType}` : '';
}

/** Upload one image and return the download URL to store in `idea.images`. */
export async function uploadIdeaImage(
  uid: string,
  ideaId: string,
  file: File
): Promise<string> {
  const name = `${crypto.randomUUID()}${extensionFor(file)}`;
  const objectRef = ref(storage, `ideas/${ideaId}/images/${name}`);
  await uploadBytes(objectRef, file, {
    contentType: file.type,
    customMetadata: { ownerId: uid },
  });
  return getDownloadURL(objectRef);
}

/**
 * Delete the object behind a download URL.
 *
 * The path is the segment between `/o/` and the query string, percent-encoded — the same
 * parse the Expo client used. Best-effort by design: callers drop the URL from the document
 * first, so a failure here leaves an orphaned object rather than a broken image reference,
 * and `cleanupOrphanedImages` collects it server-side.
 */
export async function deleteIdeaImage(downloadUrl: string): Promise<void> {
  const afterMarker = downloadUrl.split('/o/')[1];
  if (!afterMarker) throw new Error('Not a Firebase Storage download URL');
  const path = decodeURIComponent(afterMarker.split('?')[0] ?? '');
  await deleteObject(ref(storage, path));
}
